import { useEffect, useMemo, useState } from 'react';

import { toast } from 'sonner';

import { useProceduralEventForm } from './procedural-event-form';
import { effectiveAnchor } from './procedural-helpers';
import {
  KIND_META,
  LINKED_DATE_FIELDS,
  LinkedDateCalculator,
  ReviewableKind,
  UpdatedChip,
  ExtractedReasoningSection,
} from './procedural-review-fields';
import { changedProposal, suggestionToFormValues } from './procedural-suggestion-form';
import type { ProceduralTimelineEventSuggestion } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { useEditProceduralSuggestion } from '@/api/mutations/useEditProceduralSuggestion';
import {
  useAcceptProceduralSuggestion,
  useRejectProceduralSuggestion,
} from '@/api/mutations/useProceduralSuggestionAction';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { SearchableCombobox } from '@/new/components/searchable-combobox';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Switch } from '@/new/components/shadcn/switch';
import { Textarea } from '@/new/components/shadcn/textarea';
import { CivilDatePicker } from '@/new/components/timeline-calculator/civil-date-picker';
import { cn } from '@/new/lib/utils';

interface ReviewCardProps {
  suggestion: ProceduralTimelineEventSuggestion;
  eventsById: Record<string, ProceduralTimelineEvent>;
  matterId: string;
  dependentSuggestionCount: number;
}

export function ReviewCard({ suggestion, eventsById, matterId, dependentSuggestionCount }: ReviewCardProps) {
  const { mutate: accept, isLoading: isAccepting } = useAcceptProceduralSuggestion();
  const { mutate: reject, isLoading: isRejecting } = useRejectProceduralSuggestion();
  const editMutation = useEditProceduralSuggestion();
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState(false);
  const [isAcceptConfirmOpen, setIsAcceptConfirmOpen] = useState(false);

  const meta = KIND_META[suggestion.kind as ReviewableKind];
  const isBlocked = suggestion.proposedAnchorSuggestionId !== null;
  const isPending = isAccepting || isRejecting || editMutation.isLoading;
  const anchor = effectiveAnchor(suggestion);
  const isLinked = anchor.anchorEventId !== null;
  // For an Update, the fields this suggestion changes on the existing event.
  const changedSet = useMemo(
    () => new Set<string>(suggestion.kind === 'update' ? (suggestion.updatedFields ?? []) : []),
    [suggestion],
  );
  // An Update needs its linked date re-verified when either the linked-date
  // inputs (anchor/interval/counting) changed, or the start date itself moved
  // on a linked event — the latter is what a cascade recalculation produces
  // (the anchor moved, so the dependent's date must be recomputed).
  const updateNeedsCalcCheck =
    LINKED_DATE_FIELDS.some((f) => changedSet.has(f)) || (isLinked && changedSet.has('startDateTime'));
  // Whether the linked-date calculation warrants re-verification on accept:
  // always for an Add, and for an Update per the rule above.
  const shouldVerifyCalc = suggestion.kind !== 'update' || updateNeedsCalcCheck;
  // Open the calculator by default when there's a linked date worth checking: an
  // Update that needs re-verification, or an Add whose interval is set.
  const calcDefaultOpen = suggestion.kind === 'update' ? updateNeedsCalcCheck : anchor.interval !== null;

  // Name, description and time zone can't be applied to an Update (the accept
  // path applies only the enumerated updatedFields), so they're read-only there.
  const isUpdate = suggestion.kind === 'update';
  const initialValues = useMemo(() => suggestionToFormValues(suggestion), [suggestion]);
  const form = useProceduralEventForm({
    open: true,
    initialValues,
    resetKey: suggestion.id,
    eventsById,
    // Exclude the event this Update targets from anchor options (no self-anchor).
    currentEventId: suggestion.existingEventId,
  });

  // Whether the reviewer applied the calculator's date ("Use X"); cleared when
  // any calculation input changes, so the accept warning re-appears.
  const [calcConfirmed, setCalcConfirmed] = useState(false);
  useEffect(() => {
    setCalcConfirmed(false);
  }, [form.values.anchorEventId, form.values.deltaDays, form.values.countingKey]);

  const runAccept = () => {
    accept(
      { matterId, suggestionId: suggestion.id },
      {
        onSuccess: () => setIsAcceptConfirmOpen(false),
        onError: (error) => {
          toast.error(error.message || 'Failed to accept suggestion');
        },
      },
    );
  };

  // Persist only changed fields so an Update doesn't flag/overwrite untouched columns.
  const performAccept = () => {
    const changed = changedProposal(initialValues, form.values);
    if (Object.keys(changed).length === 0) {
      runAccept();
      return;
    }
    editMutation.mutate(
      { matterId, suggestionId: suggestion.id, proposal: changed },
      {
        onSuccess: () => runAccept(),
        onError: (error) => {
          toast.error(error.message || 'Failed to save changes');
        },
      },
    );
  };

  const handleAccept = () => {
    if (form.buildInput({ requireStartDate: false, requireTimeZone: false }) === null) return;
    // Warn before accepting an unconfirmed linked date; skip when the
    // calculation hasn't changed (nothing new to re-verify).
    if (isLinked && shouldVerifyCalc && !calcConfirmed) {
      setIsAcceptConfirmOpen(true);
      return;
    }
    performAccept();
  };

  const handleConfirmReject = () => {
    reject(
      { matterId, suggestionId: suggestion.id },
      {
        onSuccess: () => setIsRejectConfirmOpen(false),
        onError: (error) => {
          toast.error(error.message || 'Failed to reject suggestion');
        },
      },
    );
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-2xl flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 py-2 pb-4">
        <div className={cn('-mx-1 rounded-lg border p-3.5 py-2.5', meta.card)}>
          <div className={cn('flex items-center gap-1.5 font-medium', meta.accent)}>
            {meta.label}
            <Icon className={cn('shrink-0', meta.icon)} />
          </div>
          <p className={cn('mt-1 text-[13px] leading-tight', meta.accent)}>{meta.hint}</p>
        </div>

        {isBlocked && (
          <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            This suggestion depends on another that hasn't been reviewed yet. Resolve that one first.
          </p>
        )}

        <section className="mt-4 space-y-6">
          <div>
            <Label htmlFor="review-name">Event name</Label>
            <Input
              id="review-name"
              value={form.values.name}
              onChange={(e) => form.setName(e.target.value)}
              placeholder="e.g. Defence due"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              disabled={isUpdate}
            />
          </div>

          <div>
            <Label htmlFor="review-description">Description (optional)</Label>
            <Textarea
              id="review-description"
              value={form.values.description}
              onChange={(e) => form.setDescription(e.target.value)}
              placeholder="Optional context shown alongside the event."
              rows={3}
              disabled={isUpdate}
            />
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mb-2 flex items-center gap-1.5">
                  <Label className="mb-0">Start date</Label>
                  {changedSet.has('startDateTime') && <UpdatedChip />}
                </div>
                <CivilDatePicker
                  value={form.values.startDate}
                  onChange={form.setStartDate}
                  clearable
                  placeholder="Pick a date"
                />
              </div>
              <div>
                <div className="mb-2 flex items-center gap-1.5">
                  <Label htmlFor="review-start-time" className="mb-0">
                    Start time (optional)
                  </Label>
                </div>
                <Input
                  id="review-start-time"
                  type="time"
                  className="h-[34px]"
                  value={form.values.startTime}
                  onChange={(e) => form.setStartTime(e.target.value)}
                  disabled={form.values.startDate === null}
                />
              </div>
            </div>

            {isLinked && (
              <LinkedDateCalculator
                form={form}
                eventsById={eventsById}
                matterId={matterId}
                initialSelectedDate={initialValues.startDate}
                onCalculationConfirmed={() => setCalcConfirmed(true)}
                changedSet={changedSet}
                defaultOpen={calcDefaultOpen}
              />
            )}
          </div>

          <div className="mt-2 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mb-2 flex items-center gap-1.5">
                  <Label className="mb-0">End date (optional)</Label>
                  {changedSet.has('endDateTime') && <UpdatedChip />}
                </div>
                <CivilDatePicker
                  value={form.values.endDate}
                  onChange={form.setEndDate}
                  defaultMonth={form.values.startDate}
                  clearable
                  placeholder="Pick a date"
                />
              </div>
              <div>
                <div className="mb-2 flex items-center gap-1.5">
                  <Label htmlFor="review-end-time" className="mb-0">
                    End time (optional)
                  </Label>
                </div>
                <Input
                  id="review-end-time"
                  type="time"
                  className="h-[34px]"
                  value={form.values.endTime}
                  onChange={(e) => form.setEndTime(e.target.value)}
                  disabled={form.values.endDate === null}
                />
              </div>
            </div>

            <div>
              <Label>Time zone (optional)</Label>
              <SearchableCombobox
                options={form.timezoneOptions}
                value={form.values.timeZone}
                onChange={form.setTimeZone}
                placeholder="Pick a time zone"
                searchPlaceholder="Search time zones"
                emptyText="No matching zones."
                triggerClassName="w-full justify-between font-normal"
                containerClassName="w-full [&>span]:block"
                clearable
                disabled={isUpdate}
              />
            </div>
          </div>
        </section>

        <section className="my-8 flex items-center gap-4">
          <Switch id="review-confirmed" checked={form.values.isConfirmed} onCheckedChange={form.setIsConfirmed} />
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <label htmlFor="review-confirmed" className="cursor-pointer text-sm font-medium text-gray-800">
                Event is confirmed
              </label>
              {changedSet.has('isConfirmed') && <UpdatedChip />}
            </div>
            <p className="text-xs text-gray-500">
              Confirmed dates are finalised. Toggle confirmation if the date depends on an earlier decision in the
              timeline or is likely to change.
            </p>
          </div>
        </section>

        {suggestion.reasoning !== null && suggestion.reasoning !== '' && (
          <ExtractedReasoningSection reasoning={suggestion.reasoning} />
        )}
      </div>

      <div className="-mx-4 mt-0 flex shrink-0 items-center gap-2 border-t bg-white px-3 pt-3 pb-0">
        <Button
          variant="default"
          onClick={() => setIsRejectConfirmOpen(true)}
          disabled={isPending || isBlocked}
          className="flex-1"
        >
          <Icon className={isRejecting ? 'lucide--loader-2 animate-spin' : 'lucide--x'} />
          Reject
        </Button>
        <Button variant="primary" onClick={handleAccept} disabled={isPending || isBlocked} className="flex-1">
          <Icon className={isAccepting || editMutation.isLoading ? 'lucide--loader-2 animate-spin' : 'lucide--check'} />
          Accept
        </Button>
      </div>

      <ConfirmationModal
        isOpen={isAcceptConfirmOpen}
        onOpenChange={setIsAcceptConfirmOpen}
        title="Review the calculated date"
        onConfirm={performAccept}
        isLoading={isAccepting || editMutation.isLoading}
        confirmLabel="Continue anyway"
        // Emphasise Cancel to steer the reviewer back to the calculation
        confirmVariant="destructive"
      >
        <p className="text-sm text-gray-700">
          This date can be calculated but the calculation has not been accepted. Please review the date calculation
          before proceeding.
        </p>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={isRejectConfirmOpen}
        onOpenChange={setIsRejectConfirmOpen}
        title="Reject this suggestion?"
        onConfirm={handleConfirmReject}
        isLoading={isRejecting}
      >
        <p className="text-sm text-gray-700">This suggestion won't be applied to the timeline.</p>
        {dependentSuggestionCount > 0 && (
          <p className="mt-3 text-sm text-gray-600">
            {dependentSuggestionCount === 1
              ? 'Another suggestion depends on this one and will be unlinked.'
              : `${dependentSuggestionCount} suggestions depend on this one and will be unlinked.`}
          </p>
        )}
      </ConfirmationModal>
    </div>
  );
}
